import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import _pick from 'lodash/pick';

import { useShortSelector } from 'react-redux-app/lib/modules/core/hooks';
import { useCurrentLang } from 'react-redux-app/lib/modules/i18n/hooks';
import { useUri } from 'react-redux-app/lib/modules/routing/hooks';

import { l } from 'react-redux-app/lib/modules/i18n/utils';
import { changeLangForRequestUri } from '../../../core/i18n/utils';
import { getCampaignsStatsUrl, getRivcashForumUrl } from '../../utils/siteUrls';

import { LANGUAGE_ENGLISH, LANGUAGE_ITALIAN } from '../../../core/i18n/constants';
import { LANG_EN, LANG_IT } from '../../../core/lang/constants';
import { forumSiteIds } from '../../constants/rivcashClones';

import { getIsActive } from '../../../auth/selectors';
import { getRivcashCloneId } from '../../../init/selectors/initData';
import { getIsLeftMenuOpened } from '../../../ui/selectors';

import { actionChangeLang } from '../../../core/i18n/actions';
import { actionSwitchLeftMenu } from '../../../ui/actions';

import {
  Menu as BaseMenu,
  MenuItem,
  MenuItemLink,
  Submenu,
  SubmenuItem,
  SubmenuItemLink,
  submenu,
} from 'react-redux-app/lib/modules/menu/components';

import { theming } from 'react-redux-app/lib/modules/core/utils';
import { RRA_MENU } from 'react-redux-app/lib/modules/menu/constants';
import baseCssJson from './styles/index.css.json';


const themeApi = [
  'container',
  'menu',
  'menu_strip',
  'menu_more',
  'menuItem',
  'menuItem_open',

  'stripMenuItem',
  'stripMenuItem_hidden',
  'stripMenuItemLink',

  'icon_en',
  'icon_it',
  'icon_arrow',
  'icon_hamburger',
  'icon_openHamburger',
  'submenu_align_right',
  'submenuItemLanguage',
  'submenuItemLinkLanguage',
  'submenuItemLinkLanguage_en',
  'submenuItemLinkLanguage_it',
];

const propTypes = {
  theme: theming.getThemePropTypesShape(themeApi).isRequired,
};

const Menu = ({ theme }) => {
  const dispatch = useDispatch();

  const isLeftMenuOpened = useShortSelector(getIsLeftMenuOpened);
  const siteId = useShortSelector(getRivcashCloneId);
  const isUserActive = useShortSelector(getIsActive);
  const lang = useCurrentLang();

  const currentUri = useUri();

  const stripMenuDomNode = useRef();

  const [hiddenMenuItemsCount, setMenuItemElementsTypes] = useState(0);
  const [isMoreButtonVisible, setIsMoreButtonVisible] = useState(false);

  const onResize = (isNeedToRecheckHamburgerVisibility = false) => {
    let newHiddenMenuItemsCount = 0;

    const menuTop = stripMenuDomNode.current.getBoundingClientRect().top;
    const { childNodes } = stripMenuDomNode.current;
    for (let i = 0; i < childNodes.length; i++) {
      if (childNodes[i].getBoundingClientRect().top > menuTop) {
        newHiddenMenuItemsCount += 1;
      }
    }

    setMenuItemElementsTypes(newHiddenMenuItemsCount);
    setIsMoreButtonVisible(newHiddenMenuItemsCount > 0);

    if (isNeedToRecheckHamburgerVisibility) {
      setTimeout(() => { onResize(); }, 100);
    }
  };

  useEffect(() => {
    onResize(true);

    window.addEventListener('resize', onResize);

    return () => { window.removeEventListener('resize', onResize); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const menuItems = [];
  if (isUserActive) {
    menuItems.push(
      {
        key: 'hamburger',
        onClick: () => { dispatch(actionSwitchLeftMenu()); },
        iconBefore: theme.icon_hamburger,
      },
      {
        key: 'stats',
        to: getCampaignsStatsUrl(lang),
        text: l('menu.stats'),
      }
    );
  }
  if (forumSiteIds.includes(siteId)) {
    menuItems.push(
      {
        key: 'forum',
        to: getRivcashForumUrl(lang),
        isExternal: true,
        text: l('menu.forum'),
      }
    );
  }

  const getLangItem = (newLang, text, className) => (
    <SubmenuItem className={theme.submenuItemLanguage} key={newLang}>
      <SubmenuItemLink
        to={changeLangForRequestUri(currentUri, newLang)}
        className={classnames(theme.submenuItemLinkLanguage, className)}
        onClick={() => { dispatch(actionChangeLang(newLang)); }}
      >
        {text}
      </SubmenuItemLink>
    </SubmenuItem>
  );

  const langSubmenuItems = [
    getLangItem(LANG_IT, LANGUAGE_ITALIAN, theme.submenuItemLinkLanguage_it),
    getLangItem(LANG_EN, LANGUAGE_ENGLISH, theme.submenuItemLinkLanguage_en),
  ];

  const menuItemElementsTypes = {
    menuItemLinkElementType: MenuItemLink,
    submenuElementType: Submenu,
  };
  const langIconMap = {
    [LANG_EN]: theme.icon_en,
    [LANG_IT]: theme.icon_it,
  };

  return (
    <div className={theme.container}>
      <BaseMenu
        menuItemElementType={MenuItem}
        className={theme.menu_strip}
        menuRef={stripMenuDomNode}
      >
        {menuItems.map((menuItem, i) => (
          <MenuItem
            {...menuItemElementsTypes}
            key={menuItem.key}
            className={classnames(
              menuItem.className,
              theme.stripMenuItem,
              { [theme.stripMenuItem_hidden]: i > menuItems.length - hiddenMenuItemsCount }
            )}
            theme={{ menuItem_open: theme.menuItem_open }}
            isOpened={menuItem.iconBefore === theme.icon_hamburger && isLeftMenuOpened}
          >
            <MenuItemLink
              to={menuItem.to}
              onClick={menuItem.onClick}
              isExternal={menuItem.isExternal}
              className={theme.stripMenuItemLink}
              iconBefore={
                classnames(
                  menuItem.iconBefore,
                  {
                    [theme.icon_openHamburger]:
                      menuItem.iconBefore === theme.icon_hamburger && isLeftMenuOpened,
                  }
                )
              }
            >
              {menuItem.text}
            </MenuItemLink>
          </MenuItem>
        ))}

        <MenuItem
          {...menuItemElementsTypes}
          align="right"
          className={classnames(
            theme.stripMenuItem,
            { [theme.stripMenuItem_hidden]: hiddenMenuItemsCount > 0 }
          )}
          theme={{ menuItem_open: theme.menuItem_open }}
        >
          <MenuItemLink
            iconBefore={langIconMap[lang]}
            iconAfter={theme.icon_arrow}
            className={theme.stripMenuItemLink}
          />

          <Submenu
            theme={{ submenu_align_right: theme.submenu_align_right }}
            align={submenu.SUBMENU_ALIGN_RIGHT}
          >
            {langSubmenuItems}
          </Submenu>
        </MenuItem>
      </BaseMenu>

      {isMoreButtonVisible && (
        <BaseMenu menuItemElementType={MenuItem} className={theme.menu_more}>
          <MenuItem
            {...menuItemElementsTypes}
            align="right"
            theme={{ menuItem: theme.menuItem, menuItem_open: theme.menuItem_open }}
          >
            <MenuItemLink iconAfter={theme.icon_arrow}>
              {l('menu.more')}
            </MenuItemLink>

            <Submenu
              align={submenu.SUBMENU_ALIGN_RIGHT}
              theme={{ submenu_align_right: theme.submenu_align_right }}
            >
              {menuItems
                .filter((el, i) => i > menuItems.length - hiddenMenuItemsCount)
                .map(menuItem => (
                  <SubmenuItem key={menuItem.key}>
                    <SubmenuItemLink {..._pick(menuItem, ['to', 'onClick', 'isExternal'])}>
                      {menuItem.text}
                    </SubmenuItemLink>
                  </SubmenuItem>
                ))}

              {langSubmenuItems}
            </Submenu>
          </MenuItem>
        </BaseMenu>
      )}
    </div>
  );
};

Menu.propTypes = propTypes;

export default theming.theme(
  RRA_MENU,
  baseCssJson,
  themeApi
)(Menu);
